import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';

import { Modal } from 'react-bootstrap';

import * as S from './styles';
import { IProps } from './types';
import useIsComDomain from '@src/hooks/useIsComDomain';

import { Button } from '@atoms';
import { CheckoutAddressItem } from '@molecules';
import {
  CheckoutAddress,
  CheckoutPaymentMethod,
  CheckoutDiscount,
  CheckoutOrderNote,
  CheckoutOrderItems,
} from '@organisms';
import ContentModal from '@src/componentsNew/Modals/ContentModal/ContentModal';
import AddAddressForm from '@src/componentsNew/Form/AddAddressForm/AddAddressForm';

import { CUSTOMER_SET_DEFAULT_ADDRESS, ME_FOR_SETTING_DEFAULT_ADDRESS } from '@organisms/CheckoutInformation/queries';
import { customerSetDefaultAddress } from '@organisms/CheckoutInformation/__generated__/customerSetDefaultAddress';
import { meForSettingDefaultAddress } from '@organisms/CheckoutInformation/__generated__/meForSettingDefaultAddress';
import { CheckoutUpdateInput } from '__generated__/globalTypes';

const CheckoutInformation: React.FC<IProps> = ({
  checkout,
  freebies,
  updateCheckout,
  initialVariables,
  setCheckout,
  setNote,
  loading,
  grandTotal,
  refetch,
}: IProps) => {
  const isComDomain = useIsComDomain();
  const shippingAddress = checkout.shippingAddress;

  const [addressId, setAddressId] = useState('');
  const [codSelected, setCodSelected] = useState(false);

  const [isSelectAddressModalOpen, setIsSelectAddressModalOpen] = useState(false);
  const handleSelectAddressModalOpen = () => {
    setIsSelectAddressModalOpen(true);
  };
  const handleSelectAddressModalClose = () => {
    setIsSelectAddressModalOpen(false);
  };

  const [isAddAddressModalOpen, setIsAddAddressModalOpen] = useState(false);
  const handleAddAddressModalOpen = () => {
    setIsAddAddressModalOpen(true);
  };
  const handleAddAddressModalClose = () => {
    setIsAddAddressModalOpen(false);
  };

  const showAddAndCloseSelect = () => {
    handleAddAddressModalOpen();
    handleSelectAddressModalClose();
  };

  const validate = (values: CheckoutUpdateInput) => {
    const errors: any = {};
    const availablePoint = checkout?.user?.point?.availableAmount;
    if (availablePoint < values.usedPoint) {
      errors.usedPoint = `You only have ${availablePoint} points.`;
    }
    if (grandTotal - values.usedPoint < 1) {
      errors.usedPoint = `Maximum available point: ${grandTotal - 1}.`;
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: {
      //input value should not be null or undefined but it's needed to show placeholder
      //@ts-ignore: to show placeholder text
      usedPoint: checkout?.usedPoint?.amount ? checkout.usedPoint.amount : '',
      voucherCode: checkout.voucherCode || '',
      //@ts-ignore: to show placeholder text
      paymentType: checkout.paymentType || 'PREPAID',
    },
    validate,
    onSubmit: (values) => {
      //@ts-ignore: to show placeholder text
      if (values?.usedPoint === '') {
        values.usedPoint = 0;
      }
      if (values?.voucherCode) {
        values.voucherCode = values.voucherCode.trim();
        formik.setFieldValue('voucherCode', values.voucherCode.trim());
      }
      const variables = {
        input: {
          ...values,
        },
      };
      updateCheckout({ variables: { ...initialVariables, ...variables } });
    },
  });

  useEffect(() => {
    formik.setFieldValue('usedPoint', checkout?.usedPoint?.amount ? checkout.usedPoint.amount : '');
  }, [JSON.stringify(checkout)]);

  const [customerSetDefaultAddress] = useMutation<customerSetDefaultAddress>(CUSTOMER_SET_DEFAULT_ADDRESS);

  const [meForAddress] = useLazyQuery<meForSettingDefaultAddress>(ME_FOR_SETTING_DEFAULT_ADDRESS, {
    onCompleted: (data) => {
      if (!data?.me?.defaultBillingAddress) {
        customerSetDefaultAddress({
          variables: {
            id: addressId,
            type: 'BILLING',
          },
        });
      }
      if (!data?.me?.defaultShippingAddress) {
        customerSetDefaultAddress({
          variables: {
            id: addressId,
            type: 'SHIPPING',
          },
        });
      }
    },
  });

  const updateShippingAddress = (shippingAddressId: string) => {
    const variables = {
      input: {
        shippingAddressId,
      },
    };
    updateCheckout({ variables: { ...initialVariables, ...variables } });
    handleSelectAddressModalClose();
  };

  const createAddressCallBackFunc = (addressID: string) => {
    updateShippingAddress(addressID);
    setAddressId(addressID);
    meForAddress();

    // Close Modal
    handleSelectAddressModalClose();
    handleAddAddressModalClose();
  };

  return (
    <S.Wrapper>
      <CheckoutAddress
        shippingAddress={shippingAddress}
        handleShow={handleSelectAddressModalOpen}
        showAddAndCloseSelect={showAddAndCloseSelect}
        loading={loading}
      />

      <CheckoutPaymentMethod
        formik={formik}
        checkout={checkout}
        setCheckout={setCheckout}
        setCodSelected={setCodSelected}
        loading={loading}
      />

      {!isComDomain ? (
        <CheckoutDiscount
          formik={formik}
          checkout={checkout}
          setCheckout={setCheckout}
          codSelected={codSelected}
          loading={loading}
        />
      ) : null}

      {/* <CheckoutOrderNote setNote={setNote} /> */}
      <CheckoutOrderItems subs={checkout.subcarts} />

      {/* Add Address Modal*/}
      <ContentModal
        open={isAddAddressModalOpen}
        onClose={handleAddAddressModalClose}
        title="New Address"
        showCloseIcon={true}
      >
        <AddAddressForm createAddressCallBackFunc={createAddressCallBackFunc} />
      </ContentModal>

      <Modal show={isSelectAddressModalOpen} onHide={handleSelectAddressModalClose} size="lg">
        {/*Separate Component*/}
        <Modal.Header closeButton>
          <Modal.Title>
            <S.SelectAddressHeader>SELECT ADDRESS</S.SelectAddressHeader>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {checkout.user.addresses.map((address, i) => {
            return (
              <CheckoutAddressItem
                key={i}
                address={address}
                updateShippingAddress={updateShippingAddress}
                defaultShippingAddress={shippingAddress}
                refetch={refetch}
              />
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={showAddAndCloseSelect}>NEW ADDRESS</Button>
        </Modal.Footer>
      </Modal>
    </S.Wrapper>
  );
};

export { CheckoutInformation };
